import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";
import { functions } from "../constants/common-functions";
import { INDICATOR_CENTRE_GRADIENT } from "../constants/colors";

am4core.addLicense(LICENCE_KEY);

export default function AmChartRingIndicator({
  id,
  percentage,
  width,
  height,
  icon,
  label,
  color,
  info,
  metric = "0",
  selectedMetric = "1"
}) {
  const chart = useRef(null);
  const liveSlice = useRef(null);
  const restSlice = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const isSelected = metric === selectedMetric;

  /* CREATE CHART ONCE */
  useEffect(() => {
    const x = am4core.create(id, am4charts.PieChart);

    x.data = [
      { label: "live", value: percentage, color: color },
      { label: "rest", value: 100 - percentage, color: "rgba(100,100,100,0.1)" }
    ];

    x.radius = am4core.percent(100);
    x.innerRadius = am4core.percent(90);

    const pieSeries = x.series.push(new am4charts.PieSeries());
    pieSeries.dataFields.value = "value";
    pieSeries.dataFields.category = "label";

    pieSeries.labels.template.disabled = true;
    pieSeries.ticks.template.disabled = true;

    pieSeries.slices.template.strokeWidth = 0;
    pieSeries.slices.template.strokeOpacity = 0;
    pieSeries.slices.template.cornerRadius = 0;
    pieSeries.slices.template.pixelPerfect = true;
    pieSeries.slices.template.propertyFields.fill = "color";

    pieSeries.events.on("datavalidated", function () {
      liveSlice.current = pieSeries.dataItems.getIndex(0);
      restSlice.current = pieSeries.dataItems.getIndex(1);
    });

    chart.current = x;

    return () => {
      x.dispose();
    };
  }, [id]);

  /* ANIMATE VALUE CHANGES */
  useEffect(() => {
    if (!liveSlice.current) return;

    const timer = setTimeout(() => {
      liveSlice.current.animate(
        { property: "value", to: percentage },
        700,
        am4core.ease.cubicOut
      );

      restSlice.current.animate(
        { property: "value", to: 100 - percentage },
        700,
        am4core.ease.cubicOut
      );
    }, 2000);

    return () => clearTimeout(timer);
  }, [percentage]);

  /* STYLES */

  const containerStyle = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: width,
    height: height
  };

  const chartContainer = {
    width: width,
    height: height
  };

  const centreDisk =
  {
      position:
          "absolute",

      width:
          width * 0.66,

      height:
          width * 0.66,

      borderRadius:
          "50%",

      //--------------------------------------------------------
      // Neutral graphite instrument centre
      //--------------------------------------------------------

      background:
          `linear-gradient(
              145deg,
              #34393e 0%,
              #22272b 32%,
              #15191c 68%,
              #0b0d0f 100%
          )`,

      display:
          "flex",

      flexDirection:
          "column",

      alignItems:
          "center",

      justifyContent:
          "center",

      //--------------------------------------------------------
      // Subtle glass / instrument depth
      //--------------------------------------------------------

      border:
          "1px solid rgba(255,255,255,0.10)",

      boxShadow:
          `
          0 5px 14px rgba(0,0,0,0.24),
          inset 0 1px 0 rgba(255,255,255,0.14),
          inset 0 -6px 12px rgba(0,0,0,0.32)
          `
  };

  const iconStyle = {
    color: "rgba(255,255,255,0.85)",
    fontSize: "14px",
    marginBottom: "2px"
  };

  const percentStyle = {
    color: "#fff",
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: "22px"
  };

  const labelStyle = {
    color: "rgba(255,255,255,0.7)",
    fontSize: "11px",
    marginTop: "2px"
  };

  const infoWrapper = {
    position: "absolute",
    bottom: "3px",   // 👈 always sits just below the centre disk
    left: "50%",
    transform: "translateX(-50%)"
  };

  const infoIconStyle = {
    color: "rgba(255,255,255,0.6)",
    fontSize: "11px",
    cursor: "pointer"
  };

  const tooltipStyle = {
    position: "absolute",
    bottom: "18px",
    left: "50%",
    transform: "translateX(-50%)",
    background: "#111",
    color: "#fff",
    padding: "6px 10px",
    fontSize: "10px",
    borderRadius: "6px",
    whiteSpace: "nowrap",
    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
    opacity: showTooltip ? 1 : 0,
    pointerEvents: "none",
    transition: "opacity 0.2s ease",
    zIndex: 999
  };

  /* RENDER */

  return (
    <div style={containerStyle}>
      <div id={id} style={chartContainer}></div>

	  <div style={centreDisk}>
	    {icon && <FontAwesomeIcon icon={icon} style={iconStyle} />}

	    <div style={percentStyle}>
	      {functions.formatNumber(percentage, 0)}%
	    </div>

	    {label && <div style={labelStyle}>{label}</div>}

	    {info && (
	      <div
	        style={infoWrapper}
	        onMouseEnter={() => setShowTooltip(true)}
	        onMouseLeave={() => setShowTooltip(false)}
	      >
	        <FontAwesomeIcon icon={faCircleInfo} style={infoIconStyle} />

	        <div style={tooltipStyle}>
	          {info}
	        </div>
	      </div>
	    )}
	  </div>
    </div>
  );
}